/**
 * レジストリ配布。ここに並べた設定を、Windows の常駐エージェントがチェックインのたびに流し込む。
 * 1 行ごとに配る対象(全端末 / 動的グループ / 個別の端末)を持つ。対象が空の行は配らない(AG2)。
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";

import type { RegistryPolicy, RegistryTarget, RegistryUsers } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { jst } from "../../lib/format";
import { ALL_TARGET, EMPTY_TARGET, reasonLabel, USERS_OPTIONS, usersLabel } from "./constants";
import {
  errorText,
  useDynGroups,
  useInvalidateMdm,
  useMdmPerms,
  useRegistryPolicies,
} from "./hooks";
import { DeviceSelect, TargetEditor, TargetFields, TargetSummary } from "./RegistryTargets";

const HIVES = ["HKLM", "HKCU"];

/** 行に画面だけの id を持たせる(並べ替え・削除で index がずれないように)。 */
type Row = RegistryPolicy & { key: string };

function newRow(): Row {
  return {
    key: `new-${Math.random().toString(36).slice(2)}`,
    hive: "HKLM",
    path: "",
    name: "",
    type: "dword",
    value: 1,
    target: EMPTY_TARGET,
  };
}

function isEmpty(t: RegistryTarget): boolean {
  return !t.all && !t.groups.length && !t.devices.length;
}

export function RegistryPage() {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const { canAdmin } = useMdmPerms();
  const query = useRegistryPolicies();
  const groups = useDynGroups();
  const [rows, setRows] = useState<Row[]>([]);
  const [dirty, setDirty] = useState(false);
  const [deviceNames, setDeviceNames] = useState<Record<string, string>>({});
  const [previewDevice, setPreviewDevice] = useState<number[]>([]);
  const [tplOpen, setTplOpen] = useState(false);

  // 編集中(未保存)に再取得が返ってきても、書いたものを消さない
  useEffect(() => {
    if (!query.data || dirty) return;
    setRows(
      query.data.policies.map((p, i) => ({ ...p, target: p.target ?? EMPTY_TARGET, key: `p${i}` })),
    );
    setDeviceNames((n) => ({ ...n, ...query.data.device_names }));
  }, [query.data, dirty]);

  const groupNames = useMemo(
    () => Object.fromEntries((groups.data ?? []).map((g) => [String(g.id), g.name])),
    [groups.data],
  );
  const groupOptions = useMemo(
    () =>
      (groups.data ?? []).map((g) => ({
        value: g.id,
        label: `${g.name}(${g.members} 人)${g.active ? "" : " ※無効"}`,
      })),
    [groups.data],
  );
  const addNames = (n: Record<string, string>) => setDeviceNames((o) => ({ ...o, ...n }));

  // 対象だけを送って、行ごとの台数と、選んだ端末に当たるかを出す(保存前の確認)
  const targets = rows.map((r) => r.target);
  const preview = useQuery({
    queryKey: ["mdm", "registry-preview", targets, previewDevice[0] ?? null],
    queryFn: () => api.registryPreview(targets, previewDevice[0] ?? null),
    enabled: rows.length > 0,
  });
  const indexOf = (key: string) => rows.findIndex((r) => r.key === key);

  const save = useMutation({
    mutationFn: () =>
      api.saveRegistryPolicies(
        rows.map(({ hive, path, name, type, value, users, target }) => ({
          hive,
          path,
          name,
          type,
          value,
          // 当て先は HKCU の行だけ(HKLM の行はサーバーでも落とす)
          ...(hive === "HKCU" ? { users: users ?? "logged_on" } : {}),
          target,
        })),
      ),
    onSuccess: () => {
      void message.success("配布するポリシーを保存した");
      setDirty(false);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const patch = (key: string, v: Partial<RegistryPolicy>) => {
    setDirty(true);
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...v } : r)));
  };

  const columns: ColumnsType<Row> = [
    {
      title: "hive / HKCU の当て先",
      width: 220,
      render: (_: unknown, row) =>
        canAdmin ? (
          <Space direction="vertical" size={4}>
            <Select
              size="small"
              style={{ width: 90 }}
              value={row.hive}
              onChange={(v: string) =>
                patch(row.key, v === "HKCU" ? { hive: v, users: row.users ?? "logged_on" } : { hive: v })
              }
              options={HIVES.map((h) => ({ value: h, label: h }))}
            />
            {row.hive === "HKCU" && (
              <Select<RegistryUsers>
                size="small"
                style={{ width: 200 }}
                aria-label="HKCU の当て先"
                value={row.users ?? "logged_on"}
                onChange={(v) => patch(row.key, { users: v })}
                options={USERS_OPTIONS}
              />
            )}
          </Space>
        ) : (
          <Space direction="vertical" size={2}>
            {row.hive}
            {usersLabel(row) && <Tag>{usersLabel(row)}</Tag>}
          </Space>
        ),
    },
    {
      title: "path",
      width: 320,
      ellipsis: true,
      onCell: (row) => ({ title: row.path }),
      render: (_: unknown, row) =>
        canAdmin ? (
          <Input
            size="small"
            value={row.path}
            placeholder={"Software\\Policies\\Example"}
            onChange={(e) => patch(row.key, { path: e.target.value })}
          />
        ) : (
          row.path
        ),
    },
    {
      title: "name",
      width: 220,
      render: (_: unknown, row) =>
        canAdmin ? (
          <Input
            size="small"
            value={row.name}
            placeholder="名前"
            onChange={(e) => patch(row.key, { name: e.target.value })}
          />
        ) : (
          row.name
        ),
    },
    {
      title: "type",
      width: 160,
      render: (_: unknown, row) =>
        canAdmin ? (
          <Select
            size="small"
            style={{ width: 100 }}
            value={row.type}
            onChange={(v: "dword" | "string") =>
              patch(row.key, { type: v, value: v === "dword" ? 1 : "" })
            }
            options={[
              { value: "dword", label: "dword" },
              { value: "string", label: "string" },
            ]}
          />
        ) : (
          row.type
        ),
    },
    {
      title: "value",
      ellipsis: true,
      onCell: (row) => ({ title: String(row.value) }),
      width: 320,
      render: (_: unknown, row) => {
        if (!canAdmin) return String(row.value);
        return row.type === "dword" ? (
          <InputNumber
            size="small"
            style={{ width: 140 }}
            value={Number(row.value)}
            onChange={(v) => patch(row.key, { value: v ?? 0 })}
          />
        ) : (
          <Input
            size="small"
            value={String(row.value)}
            placeholder="値"
            onChange={(e) => patch(row.key, { value: e.target.value })}
          />
        );
      },
    },
  ];

  columns.push({
    title: "対象",
    width: 320,
    render: (_: unknown, row) => (
      <Space direction="vertical" size={4}>
        <TargetSummary target={row.target} groupNames={groupNames} deviceNames={deviceNames} />
        {canAdmin && (
          <TargetEditor
            target={row.target}
            onChange={(t) => patch(row.key, { target: t })}
            groupOptions={groupOptions}
            deviceNames={deviceNames}
            onDeviceNames={addNames}
          />
        )}
      </Space>
    ),
  });
  columns.push({
    title: "当たる台数",
    width: 110,
    render: (_: unknown, row) => {
      const n = preview.data?.counts[indexOf(row.key)];
      return n === undefined ? "—" : `${n} 台`;
    },
  });
  if (previewDevice.length) {
    columns.push({
      title: "選んだ端末",
      width: 320,
      render: (_: unknown, row) => {
        const why = preview.data?.device?.[indexOf(row.key)];
        if (why === undefined) return "—";
        return why ? <Tag color="green">{reasonLabel(why, groupNames)}</Tag> : <Tag>当たらない</Tag>;
      },
    });
  }

  if (canAdmin) {
    columns.push({
      title: "",
      width: 90,
      render: (_: unknown, row) => (
        <Popconfirm
          title="この行を消す"
          okText="消す"
          cancelText="やめる"
          onConfirm={() => {
            setDirty(true);
            setRows((rs) => rs.filter((r) => r.key !== row.key));
          }}
        >
          <Button size="small" danger>
            削除
          </Button>
        </Popconfirm>
      ),
    });
  }

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          レジストリ配布
        </Typography.Title>
        {canAdmin && (
          <Space>
            <Button onClick={() => setTplOpen(true)}>ひな形から足す</Button>
            <Button
              onClick={() => {
                setDirty(true);
                setRows((rs) => [...rs, newRow()]);
              }}
            >
              行を足す
            </Button>
            <Button type="primary" loading={save.isPending} onClick={() => save.mutate()}>
              保存
            </Button>
          </Space>
        )}
      </Flex>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="ポリシーを取得できなかった"
          description={errorText(query.error)}
        />
      )}
      {!canAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="読み取りだけ(変更には mdm:admin が要る)"
        />
      )}
      {rows.some((r) => isEmpty(r.target)) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="対象が空の行は配らない"
          description="「対象…」で全端末・動的グループ・個別の端末のどれかを選ぶ。"
        />
      )}
      {dirty && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="未保存の変更がある"
        />
      )}

      <Card size="small">
        <Flex align="center" gap={8} wrap style={{ marginBottom: 12 }}>
          <Typography.Text>端末ごとに確かめる:</Typography.Text>
          <DeviceSelect
            multiple={false}
            value={previewDevice}
            onChange={setPreviewDevice}
            names={deviceNames}
            onNames={addNames}
            placeholder="端末を選ぶと、各行が当たるかを表示する"
            style={{ width: 360 }}
          />
          {preview.isError && <Typography.Text type="danger">{errorText(preview.error)}</Typography.Text>}
        </Flex>
        <Table<Row>
          rowKey="key"
          size="small"
          columns={columns}
          dataSource={rows}
          loading={query.isFetching}
          pagination={false}
          scroll={{ x: columns.reduce((sum, column) => sum + Number(column.width), 0) }}
          locale={{ emptyText: "配布するポリシーなし" }}
        />
      </Card>

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
        path は hive を除いた部分(例 <Typography.Text code>Software\Policies\Example</Typography.Text>)。
        端末はチェックインの応答で、自分に当たる行だけを受け取り、適用結果をデバイス台帳に返す。
        対象から外した行・消した行の値は端末に残る(エージェントは消さない)。
        HKCU の行は、当て先が「ログオン中のユーザー」ならその時ログオンしているユーザーの側に書く(誰もいなければ skipped)。
        「管理者以外の全アカウント」なら、端末のローカル Administrators の直接メンバー以外で、その端末にプロファイルがある全アカウントに書く
        (ログオンしていないアカウントにも書く。まだ一度もログオンしていないアカウントには、初回ログオン後のチェックインで当たる)。
        最終更新 {jst(query.data?.updated_at ?? null)}。
      </Typography.Paragraph>

      {tplOpen && (
        <TemplateModal
          onClose={() => setTplOpen(false)}
          onAdd={(added) => {
            setDirty(true);
            setRows((rs) => [...rs, ...added]);
            setTplOpen(false);
          }}
          groupOptions={groupOptions}
          deviceNames={deviceNames}
          onDeviceNames={addNames}
        />
      )}
    </div>
  );
}

/** ひな形(Microsoft の公開文書に載っているパスだけ)から行を足す。対象もここで選ぶ。 */
function TemplateModal({
  onClose,
  onAdd,
  groupOptions,
  deviceNames,
  onDeviceNames,
}: {
  onClose: () => void;
  onAdd: (rows: Row[]) => void;
  groupOptions: { value: number; label: string }[];
  deviceNames: Record<string, string>;
  onDeviceNames: (n: Record<string, string>) => void;
}) {
  const api = useApi();
  const templates = useQuery({ queryKey: ["mdm", "registry-templates"], queryFn: () => api.registryTemplates() });
  const [key, setKey] = useState<string>();
  const [target, setTarget] = useState<RegistryTarget>(ALL_TARGET);
  const tpl = templates.data?.find((t) => t.key === key);
  return (
    <Modal
      open
      title="ひな形から足す"
      okText="足す"
      cancelText="やめる"
      okButtonProps={{ disabled: !tpl }}
      onCancel={onClose}
      onOk={() => {
        if (!tpl) return;
        onAdd(
          tpl.policies.map((p, i) => ({
            ...p,
            ...(p.hive === "HKCU" ? { users: p.users ?? "logged_on" } : {}),
            target,
            key: `tpl-${tpl.key}-${i}-${Math.random().toString(36).slice(2)}`,
          })),
        );
      }}
      width={640}
    >
      <Form layout="vertical">
        <Form.Item label="ひな形">
          <Select
            value={key}
            onChange={setKey}
            loading={templates.isLoading}
            options={(templates.data ?? []).map((t) => ({ value: t.key, label: t.title }))}
            placeholder="選ぶ"
          />
        </Form.Item>
        {templates.isError && <Alert type="error" showIcon message={errorText(templates.error)} />}
        {tpl && (
          <Alert
            type="info"
            style={{ marginBottom: 16 }}
            message={tpl.note}
            description={
              <Space direction="vertical" size={2} style={{ width: "100%" }}>
                {tpl.policies.map((p) => (
                  <Typography.Text key={p.name} code style={{ fontSize: 12 }}>
                    {p.hive}\{p.path} {p.name} = {String(p.value)}({p.type})
                  </Typography.Text>
                ))}
                {tpl.policies.some((p) => p.hive === "HKCU") && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    HKCU の行は「ログオン中のユーザー」で足す。足したあと行ごとに当て先を変えられる。
                  </Typography.Text>
                )}
                <Typography.Link href={tpl.source} target="_blank" rel="noreferrer">
                  出典(Microsoft Learn)
                </Typography.Link>
              </Space>
            }
          />
        )}
        <TargetFields
          value={target}
          onChange={setTarget}
          groupOptions={groupOptions}
          deviceNames={deviceNames}
          onDeviceNames={onDeviceNames}
        />
      </Form>
    </Modal>
  );
}
